import type { MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type Language, useLocalization } from '../../../config/localization';
import { hasManagerAccess } from '../../../constants/roles';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useOrganizationUsage } from '../../../contexts/OrganizationUsageContext';
import { useSidebar } from '../../../contexts/SidebarContext';
import Icon, { ICONS } from '../icons';

interface TokenBalanceCardProps {
  selectedLanguage: Language;
  isRefreshing?: boolean;
}

export default function TokenBalanceCard({
  selectedLanguage,
  isRefreshing = false,
}: TokenBalanceCardProps) {
  const t = useLocalization(selectedLanguage);
  const { currentOrganization, userAccess } = useOrganization();
  const { usage } = useOrganizationUsage();
  const { setActiveContext } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const topUpLabel = t.workflow?.topUpTokens || 'Top up tokens';
  const balance = usage?.remaining?.toLocaleString() || '—';
  const hasPaymentsEnabled = currentOrganization?.payment_method === 'standard'
    || currentOrganization?.payment_method === 'enterprise';
  const canTopUp = hasManagerAccess(userAccess?.currentRole || 'User') && hasPaymentsEnabled;

  const handleTopUp = () => {
    const orgId = new URLSearchParams(location.search).get('orgId');
    const billingParams = new URLSearchParams();
    if (orgId && /^\d+$/.test(orgId)) {
      billingParams.set('orgId', orgId);
    }

    const billingSearch = billingParams.toString();
    setActiveContext('organization');
    navigate(`/organization/billing${billingSearch ? `?${billingSearch}` : ''}`);
  };

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleTopUp();
  };

  const cardContent = (
    <>
      {currentOrganization && (
        <div className="mb-1 truncate text-xs font-medium text-gray-400">
          {currentOrganization.name}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {isRefreshing ? (
            <div
              data-testid="token-balance-spinner"
              className="animate-spin w-4 h-4 flex-shrink-0 border-2 border-yellow-400 border-t-transparent rounded-full"
            />
          ) : (
            <Icon name={ICONS.ZAPS} size={16} className="flex-shrink-0 text-yellow-400" />
          )}
          <span className="truncate text-sm text-gray-300">
            {t.workflow?.tokensRemaining || 'Tokens remaining'}
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <span className="text-sm font-medium text-white">
            {balance}
          </span>
        </div>
      </div>
    </>
  );

  if (!canTopUp) {
    return (
      <div
        data-testid="token-balance-card"
        className="px-4 py-3 border-b border-gray-700/50 bg-gray-800/30 flex-shrink-0"
      >
        {cardContent}
      </div>
    );
  }

  const cardLabel = `${topUpLabel}. ${currentOrganization.name}. ${t.workflow?.tokensRemaining || 'Tokens remaining'}: ${balance}`;

  return (
    <div className="relative flex-shrink-0 border-b border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50">
      <button
        type="button"
        data-testid="token-balance-card"
        aria-label={cardLabel}
        className="w-full cursor-pointer py-3 pl-4 pr-12 text-left"
        onClick={handleTopUp}
      >
        {cardContent}
      </button>
      <button
        type="button"
        aria-label={topUpLabel}
        title={topUpLabel}
        className="absolute bottom-3 right-4 flex h-6 w-6 items-center justify-center rounded-md text-gray-300 hover:bg-gray-700 hover:text-white"
        onClick={handleButtonClick}
      >
        <Icon name={ICONS.PLUS} size={14} />
      </button>
    </div>
  );
}
